// =====================
// Budget Change Requests (Phase 4 of the budgeting rework)
// =====================
//
// The single user-facing "request more budget" primitive, for both project and
// entity budgets. Approvals are event rows and request status is DERIVED from
// them under the launch policy "one APPROVED completes; one REJECTED rejects";
// the derivation lives in one API service function so a future multi-approver
// policy never touches these shapes. Amounts are numbers, matching the
// funding-link and change-order shapes these views sit beside.

import { z } from 'zod'

import type { BudgetSide } from './budget-dual-side'

export type BudgetChangeRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'

export type BudgetChangeDecision = 'APPROVED' | 'REJECTED'

export type BudgetChangeSourceType =
  | 'MANUAL'
  | 'ALLOCATION_RULE'
  | 'CONTRACT_CHANGE_ORDER'

// ---- Request bodies ----

export const CreateBudgetChangeRequestSchema = z
  .object({
    /** Target a project's budget (the 1:1 budget is resolved server-side). */
    projectId: z.string().uuid().optional(),
    /** Target a specific entity budget period (per-FY rows). */
    entityBudgetId: z.string().uuid().optional(),
    side: z.enum(['FEE', 'COST', 'HOURS']),
    /** Signed; positive asks for more budget, negative for less. */
    requestedDelta: z
      .number()
      .finite()
      .refine(v => v !== 0, { message: 'Delta cannot be zero' }),
    reason: z.string().min(1, 'Reason is required').max(2000),
  })
  .refine(v => (v.projectId ? !v.entityBudgetId : !!v.entityBudgetId), {
    message: 'Provide exactly one of projectId or entityBudgetId',
  })

export type CreateBudgetChangeRequestInput = z.infer<
  typeof CreateBudgetChangeRequestSchema
>

export const DecideBudgetChangeSchema = z.object({
  comment: z.string().max(2000).optional(),
  /**
   * Approve only: also record a ContractChangeOrder on the budget's single
   * funding contract (offered when side = FEE and exactly one link exists).
   */
  createOrder: z.boolean().optional(),
})

export type DecideBudgetChangeInput = z.infer<typeof DecideBudgetChangeSchema>

/**
 * The change-order follow-up: creating a ContractChangeOrder offers to update
 * the linked budget's fee forecast. Accepting spawns a pre-approved request
 * (sourceType CONTRACT_CHANGE_ORDER) and applies it, keeping the trail
 * symmetric with budget-initiated changes.
 */
export const CreateChangeOrderBudgetChangeSchema = z.object({
  contractChangeOrderId: z.string().uuid(),
  projectId: z.string().uuid(),
  requestedDelta: z
    .number()
    .finite()
    .refine(v => v !== 0, { message: 'Delta cannot be zero' }),
  reason: z.string().min(1, 'Reason is required').max(2000),
})

export type CreateChangeOrderBudgetChangeInput = z.infer<
  typeof CreateChangeOrderBudgetChangeSchema
>

export const ListBudgetChangeRequestsSchema = z.object({
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']).optional(),
  projectId: z.string().uuid().optional(),
  entityId: z.string().uuid().optional(),
  /**
   * Everything that belongs to one client: requests on its own budgets plus
   * requests on the budgets of its projects (the client exception queue).
   */
  clientId: z.string().uuid().optional(),
})

export type ListBudgetChangeRequestsInput = z.infer<
  typeof ListBudgetChangeRequestsSchema
>

// ---- Responses ----

export interface BudgetChangeApprovalEvent {
  id: string
  decidedBy: string
  decidedByName: string | null
  decision: BudgetChangeDecision
  comment: string | null
  createdAt: string
}

/** Approve-time hint: the request can also record a contract change order. */
export interface BudgetChangeOrderOffer {
  contractId: string
  contractTitle: string
}

export interface BudgetChangeRequestWithTrail {
  id: string
  organizationId: string
  projectBudgetId: string | null
  entityBudgetId: string | null
  side: BudgetSide
  requestedDelta: number
  reason: string
  status: BudgetChangeRequestStatus
  sourceType: BudgetChangeSourceType | null
  contractChangeOrderId: string | null
  createdBy: string
  createdByName: string | null
  createdAt: string
  updatedAt: string
  approvals: BudgetChangeApprovalEvent[]
  // Context for rendering and navigation
  projectId: string | null
  projectName: string | null
  entityId: string | null
  entityName: string | null
  budgetCurrency: string
  /** Present on PENDING FEE requests whose budget has exactly one funding link. */
  orderOffer: BudgetChangeOrderOffer | null
}
